import { useQuery } from "@tanstack/react-query";
import type { AssetType, AssetVisibility, EmbedProvider, PartnerType } from "@colab/shared";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

// Mirror of `ContentAssetRow` from apps/api/src/services/content.ts, copied
// here so the SPA doesn't reach across the workspace boundary into the
// worker's source tree. The 18-field shape is the public asset row contract.
export type ContentAssetRow = {
  id: string;
  title: string;
  description: string | null;
  asset_type: AssetType;
  is_folder: boolean;
  parent_asset_id: string | null;
  file_url: string | null;
  external_url: string | null;
  embed_provider: EmbedProvider | null;
  embed_url: string | null;
  visibility: AssetVisibility;
  partner_type_restriction: PartnerType[];
  categories: string[];
  archived_at: string | null;
  uploaded_by: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

// GET /api/portal/resources?folder=<id>
//
// Listing is visibility-gated server-side via `isAssetVisibleToPartner`
// (port of migration 0040 RLS predicate). The hook returns only the rows
// the partner can see. Folder navigation is via the `?folder=` URL param;
// the page reads it from react-router and threads it into this hook.
export function usePortalResources(folderId: string | null) {
  return useQuery<ContentAssetRow[], ApiClientError>({
    queryKey: queryKeys.portal.resources.list(folderId),
    queryFn: () =>
      apiFetch<ContentAssetRow[]>(
        folderId
          ? `/portal/resources?folder=${encodeURIComponent(folderId)}`
          : "/portal/resources",
      ),
  });
}

// GET /api/portal/resources/:id
//
// Folder-detail fetch for the breadcrumb. Enabled only when `folderId` is
// truthy; root navigation skips the fetch entirely.
export function usePortalResourceDetail(folderId: string | null) {
  return useQuery<ContentAssetRow, ApiClientError>({
    queryKey: folderId
      ? queryKeys.portal.resources.detail(folderId)
      : queryKeys.portal.resources.detail("__none__"),
    queryFn: () =>
      apiFetch<ContentAssetRow>(`/portal/resources/${encodeURIComponent(folderId!)}`),
    enabled: folderId !== null,
  });
}
